import { clientLogger } from '@/lib/clientLogger';
import { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import { InvoiceWithJobDto, JobDTO, InvoiceDto, CompanyInvoiceDTO, PaymentStatus, PaymentDTO, JobItemOperationDTO, InvoicePatchDTO } from '@/types';
import { X, Edit3, FileText, ExternalLink, Receipt, MapPin, Phone, Mail, AlertCircle, RefreshCw, DollarSign, CalendarDays, Pencil, Send, Paperclip } from 'lucide-react';
import { EditableField } from './ui/EditableField';
import { EditableAddress } from './ui/EditableAddress';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { JobDetailsModal } from './JobDetailsModal';
import { SignaturePad } from './public/SignaturePad';
import { InvoiceShareModal } from './InvoiceShareModal';
import { JobAttachmentsModal } from './JobAttachmentsModal';
import { TakePaymentButton } from './ui/TakePaymentButton';
import { JobDownPaymentRow } from './PaymentCard';
import { JobItemsSection } from './ui/JobItemsSection';
import { ItemEditorDialog } from './ui/ItemEditorDialog';
import { InvoiceBillToSheet } from './InvoiceBillToSheet';
import { ConfirmDialog } from './ui/ConfirmDialog';
import { showAlert, openDownloadUrl, getAppOrigin } from '@/lib/capacitor';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useVisualKeyboardOpen } from '@/hooks/useVisualKeyboardOpen';
import { useInvoiceEditPermissions } from '@/hooks/useInvoiceEditPermissions';
import { useCompletedJobEditLock } from '@/hooks/useCompletedJobEditLock';
import { calculateTotal } from '@/lib/invoiceCalculations';
import { useBackNavigationLayer } from '@/lib/navigation';
import { useModalLayer, LayerDepthContext } from '@/lib/layerManager';
import { useCompanyTimezone } from '@/hooks/useGlobalCompany';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { OfflineFallback } from './ui/OfflineFallback';
import { formatDateOnlyInCompanyTimeZone, formatInstantInCompanyTimeZone } from '@/lib/dateUtils';

import { createPortal } from 'react-dom';

interface InvoiceDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobId?: string;  // New: open modal for a job (will ensure invoice exists)
  invoice?: InvoiceDto | InvoiceWithJobDto | null;  // Existing: direct invoice
  onInvoiceUpdated?: (updatedInvoice: InvoiceDto | InvoiceWithJobDto) => void;
  companyId?: string;
  openedFromJob?: boolean;  // Track if opened from JobDetailsModal to prevent recursion
}

export function InvoiceDetailsModal({ isOpen, onClose, jobId, invoice: invoiceProp, onInvoiceUpdated, openedFromJob }: InvoiceDetailsModalProps) {
  const isMobileDevice = useIsMobile();
  const isKeyboardOpen = useVisualKeyboardOpen(isOpen && isMobileDevice);
  const companyTz = useCompanyTimezone();
  const { canEditInvoices, canEditCompletedInvoices } = useInvoiceEditPermissions();

  const [showJobDetailsModal, setShowJobDetailsModal] = useState(false);
  const [isViewingPdf, setIsViewingPdf] = useState(false);
  const [selectedJob, setSelectedJob] = useState<JobDTO | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showSignatureWarning, setShowSignatureWarning] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showSignatureOverrideConfirm, setShowSignatureOverrideConfirm] = useState(false);
  const [isSubmittingSignature, setIsSubmittingSignature] = useState(false);
  const [showItemEditSignatureWarning, setShowItemEditSignatureWarning] = useState(false);
  const [pendingItemOperations, setPendingItemOperations] = useState<JobItemOperationDTO[] | null>(null);
  const pendingItemPromiseRef = useRef<{ resolve: () => void; reject: (e: Error) => void } | null>(null);
  // Mobile: the compact dates card opens this sheet to edit date, due date and PO together
  const [showDatesEditor, setShowDatesEditor] = useState(false);
  const [datesDraft, setDatesDraft] = useState({ date: '', due_date: '', po: '' });
  const [isSavingDates, setIsSavingDates] = useState(false);
  const [showBillToEditor, setShowBillToEditor] = useState(false);
  const [showPdfAttachments, setShowPdfAttachments] = useState(false);

  // Internal invoice state - can come from prop or be fetched via jobId
  const [invoice, setInvoice] = useState<InvoiceDto | InvoiceWithJobDto | null>(invoiceProp || null);
  const layer = useModalLayer({
    // Keep this layer on while Job Details is stacked — disabling it on open
    // lets orphan-cleanup history.back() pop the page instead of the job.
    enabled: isOpen && !showSignatureModal && !showShareModal,
    onClose,
    historyState: { invoiceDetailsModal: true },
  });
  const handleSignatureClose = useBackNavigationLayer({
    enabled: showSignatureModal,
    onBack: () => setShowSignatureModal(false),
    state: { invoiceSignatureModal: true },
  });

  // Use mobile view based on device detection
  const useMobileView = isMobileDevice;

  const queryClient = useQueryClient();
  const isOnline = useOnlineStatus();

  // Mutation to ensure invoice exists (when opened via jobId)
  const ensureInvoiceMutation = useMutation({
    mutationFn: (jId: string) => apiService.ensureInvoiceExists(jId),
    onSuccess: (data) => {
      setInvoice(data);
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['invoice', jobId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });

  // When modal opens with jobId, ensure invoice exists
  useEffect(() => {
    if (isOpen && jobId && !invoiceProp) {
      ensureInvoiceMutation.mutate(jobId);
    }
  }, [isOpen, jobId, invoiceProp]);

  // Sync invoice from prop when it changes
  useEffect(() => {
    if (invoiceProp) {
      setInvoice(invoiceProp);
    }
  }, [invoiceProp]);

  // Fetch the associated job details
  const { data: job } = useQuery({
    queryKey: ['job', invoice?.job_id],
    queryFn: () => apiService.getJobById(invoice!.job_id!),
    enabled: isOpen && !!invoice?.job_id,
  });

  // Same key as the job's attachments modal, so ticking a file there updates this count.
  const { data: jobAttachments } = useQuery({
    queryKey: ['jobAttachments', invoice?.job_id],
    queryFn: () => apiService.getJobAttachments(invoice!.job_id!),
    enabled: isOpen && !!invoice?.job_id,
  });
  const pdfAttachmentCount = jobAttachments?.filter(a => a.include_in_pdf).length ?? 0;

  const { isCompletedJobLocked } = useCompletedJobEditLock(job ?? null, canEditCompletedInvoices, isOpen);

  // Fetch company invoice settings to get tax rate
  const { data: companyInvoiceSettings } = useQuery<CompanyInvoiceDTO | null>({
    queryKey: ['company-invoice-settings', job?.company?.id],
    // Pass the job's company: with no argument the interceptor uses the globally selected
    // company, caching another company's tax rate and branding under this job's key.
    queryFn: () => job?.company?.id ? apiService.getCompanyInvoiceSettings(job.company.id) : Promise.resolve(null),
    enabled: isOpen && !!job?.company?.id,
  });

  // Name the PDF prints when the invoice has no custom company name (mirrors InvoiceServiceClient).
  const defaultCompanyName = companyInvoiceSettings?.display_name || job?.company?.name || '';
  const renderCompanyName = (val: string) => (
    <span className="text-gray-900 font-medium text-sm">
      {val || <span className="text-gray-400">{defaultCompanyName ? `${defaultCompanyName} (default)` : 'Not set'}</span>}
    </span>
  );

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setShowJobDetailsModal(false);
      setIsViewingPdf(false);
      setSelectedJob(null);
      setShareUrl(null);
      // Reset invoice state when closing if we were using jobId mode
      if (jobId) {
        setInvoice(null);
        ensureInvoiceMutation.reset();
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Loading state - show skeleton while ensuring invoice exists
  const isLoadingInvoice = jobId && !invoiceProp && ensureInvoiceMutation.isPending;
  const invoiceError = ensureInvoiceMutation.error as Error | null;

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <div className="space-y-4 p-6">
      {/* Loading spinner with message */}
      <div className="flex flex-col items-center justify-center py-8">
        <LoadingSpinner />
        <p className="mt-4 text-sm text-gray-600">Loading invoice...</p>
      </div>
      {/* Skeleton placeholders */}
      <div className="animate-pulse space-y-4">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-full bg-gray-200" />
          <div className="space-y-2">
            <div className="h-5 w-40 bg-gray-200 rounded" />
            <div className="h-4 w-24 bg-gray-200 rounded" />
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="space-y-3">
            <div className="h-32 bg-gray-200 rounded-lg" />
            <div className="h-24 bg-gray-200 rounded-lg" />
          </div>
          <div className="lg:col-span-2 h-64 bg-gray-200 rounded-lg" />
        </div>
      </div>
    </div>
  );

  // Error component with retry
  const ErrorDisplay = () => (
    <div className="flex flex-col items-center justify-center p-8 text-center">
      <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
        <AlertCircle className="h-8 w-8 text-red-600" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed to Load Invoice</h3>
      <p className="text-sm text-gray-600 mb-4 max-w-sm">
        {(invoiceError as any)?.response?.data?.message || invoiceError?.message || 'An error occurred while loading the invoice.'}
      </p>
      <div className="flex gap-3">
        <button
          onClick={() => ensureInvoiceMutation.mutate(jobId!)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Retry
        </button>
        <button
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );

  // Show loading or error states before main content
  if (isLoadingInvoice || invoiceError || !invoice) {
    return createPortal(
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4" style={layer.style}>
        <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-100">
                <Receipt className="h-5 w-5 text-green-600" />
              </div>
              <span className="font-semibold text-gray-900">Invoice</span>
            </div>
            <button onClick={onClose} className="p-2 text-gray-500 hover:bg-gray-100 rounded-full">
              <X className="h-5 w-5" />
            </button>
          </div>
          {invoiceError ? (
            <ErrorDisplay />
          ) : !isOnline && !invoice ? (
            // Offline and this invoice isn't cached — ensureInvoiceExists is a paused
            // mutation that would otherwise spin the skeleton forever.
            <OfflineFallback
              message="This invoice isn't saved for offline use. Reconnect to load it."
              onRetry={() => jobId && ensureInvoiceMutation.mutate(jobId)}
              onBack={onClose}
            />
          ) : (
            <LoadingSkeleton />
          )}
        </div>
      </div>,
      document.body
    );
  }

  const handleViewPdf = async () => {
    setIsViewingPdf(true);
    try {
      if (job?.id) {
        const { url } = await apiService.getInvoicePdfUrl(job.id, invoice.id);
        await openDownloadUrl(url);
        return;
      }

      // Standalone invoice (no job) – use invoice id
      const { url } = await apiService.getInvoicePdfUrl(undefined, invoice.id, invoice.id);
      await openDownloadUrl(url);
    } catch (error) {
      clientLogger.error('Failed to get PDF URL:', error);
      await showAlert('Failed to load PDF. Please try again.', 'Error');
    } finally {
      setIsViewingPdf(false);
    }
  };

  const handleViewJob = () => {
    if (job) {
      // If we came from JobDetailsModal, just close this modal (go back) instead of opening another
      if (openedFromJob) {
        onClose();
        return;
      }
      setSelectedJob(job);
      setShowJobDetailsModal(true);
    }
  };

  const handleShareInvoice = async () => {
    setIsSharing(true);
    try {
      // Creates a new token or reuses existing active one
      const tokenResult = await apiService.createInvoiceToken(invoice.id);
      const publicUrl = tokenResult.short_code
        ? `${getAppOrigin()}/s/${tokenResult.short_code}`
        : `${getAppOrigin()}/public/invoice/${invoice.id}/${tokenResult.token}`;
      setShareUrl(publicUrl);
      setShowShareModal(true);
    } catch (error) {
      clientLogger.error('Failed to create share link:', error);
      await showAlert('Failed to create share link. Please try again.', 'Share Error');
    } finally {
      setIsSharing(false);
    }
  };

  const handleCloseShareModal = () => {
    setShowShareModal(false);
  };

  const handleInvoiceSent = async () => {
    if (!invoice) return;
    try {
      await apiService.markInvoiceSent(invoice.id);
      const now = new Date().toISOString();
      setInvoice(prev => prev ? { ...prev, sent_at: now, sent: true } : prev);
      if (onInvoiceUpdated && invoice) {
        onInvoiceUpdated({ ...invoice, sent_at: now, sent: true });
      }
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    } catch (error) {
      clientLogger.error('Failed to mark invoice as sent:', error);
    }
  };



  const handleConfirmSignatureVoiding = () => {
    setShowSignatureWarning(false);
  };

  const handleCancelSignatureVoiding = () => {
    setShowSignatureWarning(false);
  };

  // Handler for opening signature modal
  const handleSignInvoice = () => {
    // Only show "Replace Signature?" if there is a valid, non-voided signature
    if (invoice.signature && !invoice.is_signature_voided) {
      setShowSignatureOverrideConfirm(true);
    } else {
      setShowSignatureModal(true);
    }
  };

  // Handler for signature submission
  const handleSignatureSubmit = async (signaturePath: string, signedByName: string) => {
    setIsSubmittingSignature(true);
    try {
      await apiService.submitInvoiceSignature(invoice.id, signaturePath, signedByName);

      // Refresh invoice data
      queryClient.invalidateQueries({ queryKey: ['invoice', invoice.id] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });

      // Update local state with new signature — also clear is_signature_voided so the
      // signed status and preview render immediately without waiting for a refetch.
      const now = new Date().toISOString();
      setInvoice(prev =>
        prev ? { ...prev, signature: signaturePath, signed_by_name: signedByName, is_signature_voided: false, viewed_at: now, signed_at: now } : prev
      );

      if (onInvoiceUpdated && invoice) {
        onInvoiceUpdated({ ...invoice, signature: signaturePath, signed_by_name: signedByName, is_signature_voided: false, viewed_at: now, signed_at: now });
      }

      setShowSignatureModal(false);
    } catch (error) {
      clientLogger.error('Failed to submit signature:', error);
      await showAlert('Failed to save signature. Please try again.', 'Error');
    } finally {
      setIsSubmittingSignature(false);
    }
  };

  const handleConfirmSignatureOverride = () => {
    setShowSignatureOverrideConfirm(false);
    setShowSignatureModal(true);
  };

  const handleCancelSignatureOverride = () => {
    setShowSignatureOverrideConfirm(false);
  };

  // Tax rate from company settings for calculation functions
  const taxRate = companyInvoiceSettings?.tax_rate ?? 0;

  // Amount shown in the share SMS/email: what is still owed, or the total once nothing is owed.
  // Undefined at 0 so a zero-total invoice never advertises "$0.00".
  const shareAmountLabel = (() => {
    const items = job?.items || invoice?.items || [];
    const total = calculateTotal(items, taxRate);
    const paid = (job?.payments ?? []).reduce((sum: number, p: PaymentDTO) => sum + p.amount, 0);
    const due = Math.max(0, total - paid);
    const amount = due > 0 ? due : total;
    return amount > 0 ? `$${amount.toFixed(2)}` : undefined;
  })();

  const canEditInvoice = canEditInvoices && !isCompletedJobLocked;

  // Whether item editing is allowed (needs job + permission)
  const canEditItems = !!job && canEditInvoice;

  // Handler for job item changes (add/edit/delete) - routes through Job PATCH API
  const handleJobItemsChange = async (operations: JobItemOperationDTO[]) => {
    if (!job) return;

    // Check if invoice has a non-voided signature — warn before proceeding,
    // but only if these operations would actually change the total.
    if (invoice.signature && !invoice.is_signature_voided) {
      const wouldChangeAmount = operations.some(op => {
        if (op.operation === 'Add' || op.operation === 'Remove') return true;
        if (op.operation === 'Edit' && op.item.id) {
          // Compare against existing item to see if financial fields changed
          const existing = (job.items || []).find((i: any) => i.id === op.item.id);
          if (!existing) return true;
          return (
            existing.price !== op.item.price ||
            existing.quantity !== op.item.quantity ||
            existing.discount !== op.item.discount ||
            existing.taxable !== op.item.taxable
          );
        }
        return false;
      });

      if (wouldChangeAmount) {
        // Return a promise that stays pending until the user confirms or cancels.
        // This keeps the JobItemsSection form open while the dialog is visible.
        return new Promise<void>((resolve, reject) => {
          setPendingItemOperations(operations);
          pendingItemPromiseRef.current = { resolve, reject };
          setShowItemEditSignatureWarning(true);
        });
      }
    }

    await executeItemOperations(operations);
  };

  const executeItemOperations = async (operations: JobItemOperationDTO[]) => {
    if (!job) return;

    // Apply operations to current items to compute new items list
    let newItems = [...(job.items || [])];
    for (const op of operations) {
      if (op.operation === 'Add') {
        newItems.push(op.item);
      } else if (op.operation === 'Remove' && op.item.id) {
        newItems = newItems.filter(item => (item as any).id !== op.item.id);
      } else if (op.operation === 'Edit' && op.item.id) {
        newItems = newItems.map(item =>
          (item as any).id === op.item.id ? op.item : item
        );
      }
    }

    try {
      const updatedInvoice = await apiService.patchInvoice(invoice.id, {
        item_operations: operations,
      });

      const refreshedJob = await apiService.getJobById(job.id);

      // Update the job query cache with the fresh data
      queryClient.setQueryData(['job', job.id], refreshedJob);
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', invoice.id] });

      // Update local invoice to match backend state after the item patch.
      setInvoice(updatedInvoice);

      if (onInvoiceUpdated) {
        onInvoiceUpdated(updatedInvoice);
      }
    } catch (error) {
      clientLogger.error('Failed to update job items:', error);
      throw error;
    }
  };

  const handleConfirmItemEditSignatureWarning = async () => {
    setShowItemEditSignatureWarning(false);
    if (pendingItemOperations) {
      try {
        await executeItemOperations(pendingItemOperations);
        pendingItemPromiseRef.current?.resolve();
      } catch (e) {
        pendingItemPromiseRef.current?.reject(e as Error);
      } finally {
        setPendingItemOperations(null);
        pendingItemPromiseRef.current = null;
      }
    }
  };

  const handleCancelItemEditSignatureWarning = () => {
    setShowItemEditSignatureWarning(false);
    setPendingItemOperations(null);
    // Reject so JobItemsSection's handleSaveItem catch fires → form stays open
    pendingItemPromiseRef.current?.reject(new Error('cancelled'));
    pendingItemPromiseRef.current = null;
  };

  // Helper function for payment status display
  const getPaymentStatusDisplay = (status: PaymentStatus | undefined) => {
    switch (status) {
      case 'PaidFully':
        return { label: 'Paid in Full', color: 'bg-green-100 text-green-800' };
      case 'PartiallyPaid':
        return { label: 'Partially Paid', color: 'bg-amber-100 text-amber-800' };
      case 'PaymentFailed':
        return { label: 'Payment Failed', color: 'bg-red-100 text-red-800' };
      case 'NotPaid':
      default:
        return { label: 'Not Paid', color: 'bg-gray-100 text-gray-800' };
    }
  };

  // Handler for saving individual invoice fields (client info, dates)
  const handleFieldSave = async (field: string | InvoicePatchDTO, value?: any) => {
    try {
      const patch: InvoicePatchDTO = typeof field === 'string' ? { [field]: value } as InvoicePatchDTO : field;
      const updatedInvoice = await apiService.patchInvoice(invoice.id, patch);

      // Refresh related queries
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', invoice.id] });

      // Update internal state
      setInvoice(updatedInvoice);

      if (onInvoiceUpdated) {
        onInvoiceUpdated(updatedInvoice);
      }
    } catch (error) {
      clientLogger.error(`Failed to update ${typeof field === 'string' ? field : 'invoice'}:`, error);
      await showAlert(`Failed to update. Please try again.`, 'Error');
    }
  };

  const invoiceAddress = invoice.client_address || invoice.address || job?.address || '';
  const invoiceUnitName = invoice.client_unit_name || job?.unit_name || '';

  const renderAddressField = () => (
    <div>
      <label className="text-xs font-semibold text-emerald-600 uppercase tracking-wide flex items-center gap-1 mb-1">
        <MapPin className="h-3 w-3" /> Address
      </label>
      <EditableAddress
        value={invoiceAddress}
        showUnitField={true}
        unitValue={invoiceUnitName}
        placeholder="Add address..."
        disabled={!canEditInvoice}
        onSave={async (address, unitName) => {
          await handleFieldSave({
            client_address: address || null,
            client_unit_name: unitName || null,
          });
        }}
      />
    </div>
  );


  // ==================== MOBILE VIEW ====================
  if (useMobileView) {
    // When the job details modal is open, hide the invoice modal's backdrop so the
    // side menu is not blurred / blocked. Only the JobDetailsModal renders.
    if (showJobDetailsModal) {
      return createPortal(<LayerDepthContext.Provider value={layer.depth}>{renderModals()}</LayerDepthContext.Provider>, document.body);
    }

    return createPortal(
      <LayerDepthContext.Provider value={layer.depth}>
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-0" style={layer.style}>
        <div className="bg-gray-50 w-full h-full overflow-hidden flex flex-col relative safe-area-top">

          {/* Header - Compact */}
          <div className="bg-white border-b border-gray-200 z-10 shrink-0 touch-none">
            <div className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-100">
                  <Receipt className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900 leading-tight">
                    Invoice #{invoice.invoice_number}
                  </h2>
                  <p className="text-xs text-gray-500">
                    {formatDateOnlyInCompanyTimeZone(invoice.date, companyTz)}
                    {invoice.due_date && ` • Due ${formatDateOnlyInCompanyTimeZone(invoice.due_date, companyTz)}`}
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 text-gray-500 hover:bg-gray-100 rounded-full">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Main Content - Scrollable */}
          <div className={`flex-1 overflow-y-auto overscroll-y-none ${isKeyboardOpen ? '' : 'pb-footer-safe'}`}>
            <div className="p-4 space-y-4">


              {/* Company name printed on this invoice */}
              <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                <label className="text-xs font-semibold text-indigo-600 uppercase tracking-wide block mb-1">Company Name</label>
                <EditableField
                  value={invoice.custom_company_name || ''}
                  type="text"
                  placeholder={defaultCompanyName || 'Add company name...'}
                  disabled={!canEditInvoice}
                  onSave={(val) => handleFieldSave('custom_company_name', val || null)}
                  inline={true}
                  renderValue={renderCompanyName}
                />
              </div>

              {/* Card 1: Dates - compact summary; editing opens a sheet with all three fields */}
              <button
                type="button"
                disabled={!canEditInvoice}
                onClick={() => {
                  setDatesDraft({
                    date: invoice.date ? invoice.date.split('T')[0] : '',
                    due_date: invoice.due_date ? invoice.due_date.split('T')[0] : '',
                    po: invoice.po || '',
                  });
                  setShowDatesEditor(true);
                }}
                className="w-full bg-white px-3 py-2.5 rounded-xl border border-gray-200 shadow-sm text-left flex items-center gap-3 active:bg-gray-50 disabled:active:bg-white"
              >
                <div className="grid grid-cols-3 gap-2 flex-1 min-w-0">
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold text-blue-600 uppercase tracking-wide">Date</div>
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {invoice.date ? formatDateOnlyInCompanyTimeZone(invoice.date, companyTz) : <span className="text-gray-400">Not set</span>}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold text-blue-600 uppercase tracking-wide">Due</div>
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {invoice.due_date ? formatDateOnlyInCompanyTimeZone(invoice.due_date, companyTz) : <span className="text-gray-400">Not set</span>}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold text-blue-600 uppercase tracking-wide">PO #</div>
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {invoice.po || <span className="text-gray-400">—</span>}
                    </div>
                  </div>
                </div>
                {canEditInvoice && (
                  <span className="p-1.5 rounded-lg text-gray-400 bg-gray-50 shrink-0">
                    <Pencil className="h-4 w-4" />
                  </span>
                )}
              </button>

              {showDatesEditor && (
                <ItemEditorDialog
                  title="Invoice Details"
                  icon={CalendarDays}
                  onClose={() => setShowDatesEditor(false)}
                  footer={
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setShowDatesEditor(false)}
                        className="flex-1 py-2.5 text-sm font-medium rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        disabled={isSavingDates}
                        onClick={async () => {
                          const patch: InvoicePatchDTO = {};
                          const currentDate = invoice.date ? invoice.date.split('T')[0] : '';
                          const currentDue = invoice.due_date ? invoice.due_date.split('T')[0] : '';
                          if (datesDraft.date && datesDraft.date !== currentDate) patch.date = datesDraft.date;
                          if (datesDraft.due_date !== currentDue) patch.due_date = datesDraft.due_date || null;
                          if (datesDraft.po !== (invoice.po || '')) patch.po = datesDraft.po || null;
                          if (Object.keys(patch).length > 0) {
                            setIsSavingDates(true);
                            await handleFieldSave(patch);
                            setIsSavingDates(false);
                          }
                          setShowDatesEditor(false);
                        }}
                        className="flex-1 py-2.5 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-1.5"
                      >
                        {isSavingDates && <LoadingSpinner />}
                        Save
                      </button>
                    </div>
                  }
                >
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-semibold text-blue-600 uppercase tracking-wide block mb-1">Invoice Date</label>
                      <input
                        type="date"
                        className="input-field text-sm"
                        value={datesDraft.date}
                        onChange={(e) => setDatesDraft(d => ({ ...d, date: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-blue-600 uppercase tracking-wide block mb-1">Due Date</label>
                      <input
                        type="date"
                        className="input-field text-sm"
                        value={datesDraft.due_date}
                        onChange={(e) => setDatesDraft(d => ({ ...d, due_date: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-blue-600 uppercase tracking-wide block mb-1">PO #</label>
                      <input
                        type="text"
                        className="input-field text-sm"
                        placeholder="Add PO number..."
                        value={datesDraft.po}
                        onChange={(e) => setDatesDraft(d => ({ ...d, po: e.target.value }))}
                      />
                    </div>
                  </div>
                </ItemEditorDialog>
              )}

              {/* Card 2: Bill To - compact summary; editing opens a sheet with all client details */}
              <button
                type="button"
                disabled={!canEditInvoice}
                onClick={() => setShowBillToEditor(true)}
                className="w-full bg-white px-3 py-2.5 rounded-xl border border-gray-200 shadow-sm text-left flex items-center gap-3 active:bg-gray-50 disabled:active:bg-white"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wide">Bill To</div>
                  <div className="text-sm font-semibold text-gray-900 truncate">
                    {invoice.client_name || <span className="text-gray-400 font-normal italic">No name</span>}
                  </div>
                </div>
                {canEditInvoice && (
                  <span className="p-1.5 rounded-lg text-gray-400 bg-gray-50 shrink-0">
                    <Pencil className="h-4 w-4" />
                  </span>
                )}
              </button>

              {showBillToEditor && (
                <InvoiceBillToSheet
                  initial={{
                    client_name: invoice.client_name || '',
                    client_phone: invoice.client_phone || '',
                    client_email: invoice.client_email || '',
                    client_address: invoiceAddress,
                    client_unit_name: invoiceUnitName,
                  }}
                  onClose={() => setShowBillToEditor(false)}
                  onSave={(patch) => handleFieldSave(patch)}
                />
              )}

              {/* Card 3: Invoice Items & Summary - Editable */}
              {job ? (
                <JobItemsSection
                  items={job.items || []}
                  companyId={job.company?.id}
                  taxRate={taxRate}
                  defaultItemTaxable={companyInvoiceSettings?.default_item_taxable ?? true}
                  onItemsChange={handleJobItemsChange}
                  isEditable={canEditItems}
                  collapsible
                  defaultCollapsed
                />
              ) : (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="p-3 text-sm text-gray-500 text-center">No job linked</div>
                </div>
              )}

              {/* Payment Summary (outside the collapsible items) */}
              {job && (() => {
                const items = job.items || invoice.items || [];
                const total = calculateTotal(items, taxRate);
                const totalPaid = (job.payments ?? []).reduce((s: number, p: PaymentDTO) => s + p.amount, 0);
                const balance = total - totalPaid;
                const hasPayments = (job.payments?.length ?? 0) > 0;
                // Down payment row renders nothing when there is none and it can't be added
                const showDownPayment = job.deposit_amount != null || (canEditInvoice && balance > 0);
                if (!hasPayments && !showDownPayment) return null;
                return (
                  <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                    <div className="space-y-1">
                      {hasPayments && (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Paid</span>
                            <span className="text-green-600 font-medium">-${totalPaid.toFixed(2)}</span>
                          </div>
                          <div className="border-t border-gray-200 mt-1 pt-1">
                            <div className="flex justify-between text-base font-bold">
                              <span className="text-gray-900">Balance Due</span>
                              <span className={balance <= 0 ? 'text-green-600' : 'text-red-600'}>
                                ${Math.max(0, balance).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                      {showDownPayment && (
                        <div className={hasPayments ? 'pt-2' : ''}>
                          <JobDownPaymentRow job={job} disabled={!canEditInvoice} />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}


              {/* Notes */}
              <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                <div>
                  <label className="text-xs font-semibold text-amber-600 uppercase tracking-wide block mb-1">Notes</label>
                  <EditableField
                    value={invoice.notes || ''}
                    type="textarea"
                    placeholder="Add notes..."
                    disabled={!canEditInvoice}
                    onSave={(val) => handleFieldSave('notes', val || null)}
                    inline={true}
                    renderValue={(val) => (
                      <span className="text-gray-900 text-sm whitespace-pre-wrap">{val || <span className="text-gray-400 italic">Notes will appear at bottom of your invoice</span>}</span>
                    )}
                  />
                </div>
              </div>

              {/* Card 5: Invoice Status */}
              <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                <div className="space-y-3">
                  {/* Payment Status */}
                  {job && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">Payment Status</span>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getPaymentStatusDisplay(job.payment_status).color}`}>
                        {getPaymentStatusDisplay(job.payment_status).label}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Viewed</span>
                    <span className={`font-medium ${invoice.viewed_at ? 'text-green-600' : 'text-gray-400'}`}>
                      {invoice.viewed_at ? formatInstantInCompanyTimeZone(invoice.viewed_at, companyTz) : 'Not viewed'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Sent</span>
                    <span className={`font-medium ${invoice.sent_at ? 'text-purple-600' : 'text-gray-400'}`}>
                      {invoice.sent_at
                        ? formatInstantInCompanyTimeZone(invoice.sent_at, companyTz)
                        : 'Not sent'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Signed</span>
                    <span className={`font-medium ${(invoice.signature && !invoice.is_signature_voided) ? 'text-green-600' : 'text-gray-400'}`}>
                      {(invoice.signature && !invoice.is_signature_voided) ? 'Yes' : 'Not signed'}
                    </span>
                  </div>
                  {invoice.signed_at && !invoice.is_signature_voided && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Signed at</span>
                      <span className="font-medium text-gray-900">
                        {formatInstantInCompanyTimeZone(invoice.signed_at, companyTz)}
                      </span>
                    </div>
                  )}
                  {invoice.signature && !invoice.is_signature_voided && (
                    <div className="border border-gray-200 rounded-lg p-2 bg-gray-50">
                      {invoice.signature.startsWith('data:') ||
                        invoice.signature.startsWith('http') ||
                        invoice.signature.startsWith('blob:') ? (
                        <img
                          src={invoice.signature}
                          alt="Client signature"
                          className="max-w-full h-20 object-contain mx-auto"
                        />
                      ) : (
                        <svg
                          viewBox="0 0 400 200"
                          className="w-full h-20"
                          role="img"
                          aria-label="Client signature"
                        >
                          <path
                            d={invoice.signature}
                            fill="none"
                            stroke="black"
                            strokeWidth="2"
                          />
                        </svg>
                      )}
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>

          {/* Sticky Footer Action Bar — hidden, not unmounted, while the keyboard is open:
              TakePaymentButton below owns the payment modal's open state. */}
          <div className={`absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 safe-area-bottom-padded z-20 touch-none ${isKeyboardOpen ? 'hidden' : ''}`}>
            <div className="grid grid-cols-6 gap-1 px-2 pt-2">
              <button
                onClick={handleSignInvoice}
                disabled={isSubmittingSignature}
                className="flex flex-col items-center justify-center gap-1 p-1 rounded-lg hover:bg-gray-50 text-gray-600 active:bg-gray-100 transition-colors disabled:opacity-50"
              >
                <div className="flex items-center justify-center w-10 h-10 rounded-full border border-gray-300 bg-white shadow-sm">
                  {isSubmittingSignature ? (
                    <LoadingSpinner />
                  ) : (
                    <Edit3 className="h-5 w-5 text-gray-700" />
                  )}
                </div>
                <span className="text-[10px] font-medium text-gray-600">Sign</span>
              </button>

              <button
                onClick={handleViewPdf}
                disabled={isViewingPdf}
                className="flex flex-col items-center justify-center gap-1 p-1 rounded-lg hover:bg-gray-50 text-gray-600 active:bg-gray-100 transition-colors disabled:opacity-50"
              >
                <div className="flex items-center justify-center w-10 h-10 rounded-full border border-gray-300 bg-white shadow-sm">
                  {isViewingPdf ? (
                    <LoadingSpinner />
                  ) : (
                    <FileText className="h-5 w-5 text-gray-700" />
                  )}
                </div>
                <span className="text-[10px] font-medium text-gray-600">{isViewingPdf ? 'Generating...' : 'PDF'}</span>
              </button>

              <button
                onClick={() => setShowPdfAttachments(true)}
                disabled={!invoice.job_id}
                className="flex flex-col items-center justify-center gap-1 p-1 rounded-lg hover:bg-gray-50 text-gray-600 active:bg-gray-100 transition-colors disabled:opacity-30"
              >
                <div className="relative flex items-center justify-center w-10 h-10 rounded-full border border-gray-300 bg-white shadow-sm">
                  <Paperclip className="h-5 w-5 text-gray-700" />
                  {pdfAttachmentCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-green-600 text-white text-[10px] font-semibold flex items-center justify-center">{pdfAttachmentCount}</span>
                  )}
                </div>
                <span className="text-[10px] font-medium text-gray-600">Attach</span>
              </button>

              <button
                onClick={handleShareInvoice}
                disabled={isSharing}
                className="flex flex-col items-center justify-center gap-1 p-1 rounded-lg hover:bg-gray-50 text-gray-600 active:bg-gray-100 transition-colors disabled:opacity-50"
              >
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-600 shadow-sm">
                  <Send className="h-5 w-5 text-white" />
                </div>
                <span className="text-[10px] font-semibold text-green-700">{isSharing ? 'Wait...' : 'Send'}</span>
              </button>

              {job ? (
                <TakePaymentButton
                  job={job}
                  disabled={job.payment_status === 'PaidFully' || !job.total}
                  onPaymentComplete={() => {
                    queryClient.invalidateQueries({ queryKey: ['job', invoice?.job_id] });
                    queryClient.invalidateQueries({ queryKey: ['jobs'] });
                  }}
                  renderCustom={(onClick: () => void, isDisabled: boolean) => (
                    <button
                      onClick={onClick}
                      disabled={isDisabled}
                      className={`flex flex-col items-center justify-center gap-1 p-1 rounded-lg hover:bg-gray-50 text-gray-600 active:bg-gray-100 transition-colors ${isDisabled ? 'opacity-50' : ''}`}
                    >
                      <div className={`flex items-center justify-center w-10 h-10 rounded-full border shadow-sm ${isDisabled ? 'border-gray-200 bg-gray-50' : 'border-green-300 bg-green-50'}`}>
                        <DollarSign className={`h-5 w-5 ${isDisabled ? 'text-gray-400' : 'text-green-600'}`} />
                      </div>
                      <span className="text-[10px] font-medium text-gray-600">Payment</span>
                    </button>
                  )}
                />
              ) : (
                <div className="flex flex-col items-center justify-center gap-1 p-1 opacity-30">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full border border-gray-300 bg-white">
                    <DollarSign className="h-5 w-5 text-gray-500" />
                  </div>
                  <span className="text-[10px] font-medium text-gray-500">Payment</span>
                </div>
              )}

              {job ? (
                <button
                  onClick={handleViewJob}
                  className="flex flex-col items-center justify-center gap-1 p-1 rounded-lg hover:bg-gray-50 text-gray-600 active:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center justify-center w-10 h-10 rounded-full border border-gray-300 bg-white shadow-sm">
                    <ExternalLink className="h-5 w-5 text-gray-700" />
                  </div>
                  <span className="text-[10px] font-medium text-gray-600">Job</span>
                </button>
              ) : (
                <div className="flex flex-col items-center justify-center gap-1 p-1 opacity-30">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full border border-gray-300 bg-white">
                    <ExternalLink className="h-5 w-5 text-gray-500" />
                  </div>
                  <span className="text-[10px] font-medium text-gray-500">No Job</span>
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Modals */}
        {renderModals()}
      </div>
      </LayerDepthContext.Provider>,
      document.body
    );
  }

  // ==================== DESKTOP VIEW ====================
  function renderModals() {
    return (
      <>
        {/* Job Details Modal */}
        <JobDetailsModal
          isOpen={showJobDetailsModal}
          onClose={() => {
            setShowJobDetailsModal(false);
            setSelectedJob(null);
          }}
          job={selectedJob}
          onJobUpdated={(updatedJob) => {
            // Update the selected job with fresh data
            setSelectedJob(updatedJob);
            // Update the query cache directly (no extra GET request needed)
            queryClient.setQueryData(['job', invoice!.job_id], updatedJob);
          }}
          openedFromInvoice={true}
        />

        {/* Signature Voiding Confirmation Modal */}
        {showSignatureWarning && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={handleCancelSignatureVoiding} />
            <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-4 relative z-10 animate-in zoom-in-95">
              <div className="flex items-start gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                  <svg className="h-5 w-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Void Signature?
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Editing this invoice will void the client's signature. This cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                  onClick={handleCancelSignatureVoiding}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 transition-colors"
                  onClick={handleConfirmSignatureVoiding}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Signature Override Confirmation Modal */}
        {showSignatureOverrideConfirm && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={handleCancelSignatureOverride} />
            <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-4 relative z-10 animate-in zoom-in-95">
              <div className="flex items-start gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                  <svg className="h-5 w-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Replace Signature?
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    A signature already exists on this invoice. Do you want to replace it with a new one?
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                  onClick={handleCancelSignatureOverride}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="flex-1 px-4 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors"
                  onClick={handleConfirmSignatureOverride}
                >
                  Replace
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Item Edit Signature Warning Modal */}
        <ConfirmDialog
            isOpen={showItemEditSignatureWarning}
            onClose={handleCancelItemEditSignatureWarning}
            onConfirm={handleConfirmItemEditSignatureWarning}
            title="Editing May Void Signature"
            message="This invoice has been signed by the customer. Editing items may change the total, which will void their signature."
            confirmText="Continue"
        />

        {/* Signature Pad Modal */}
        {showSignatureModal && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={handleSignatureClose} />
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 relative z-10 animate-in zoom-in-95">
              <SignaturePad
                onSignatureComplete={handleSignatureSubmit}
                onCancel={handleSignatureClose}
                initialSignaturePath={(invoice?.signature && !invoice?.is_signature_voided) ? invoice.signature : undefined}
                initialSignerName={(invoice?.signature && !invoice?.is_signature_voided) ? invoice.signed_by_name ?? undefined : undefined}
              />
            </div>
          </div>
        )}

        {invoice?.job_id && (
          <JobAttachmentsModal
            isOpen={showPdfAttachments}
            onClose={() => setShowPdfAttachments(false)}
            jobId={invoice.job_id}
            uploadsIncludedInPdf
            onAttachmentsUpdated={() => queryClient.invalidateQueries({ queryKey: ['jobAttachments', invoice.job_id] })}
          />
        )}

        {/* Invoice Share Modal */}
        {shareUrl && (
          <InvoiceShareModal
            isOpen={showShareModal}
            onClose={handleCloseShareModal}
            onSent={handleInvoiceSent}
            shareUrl={shareUrl}
            recipientPhone={invoice?.client_phone || ''}
            recipientName={invoice?.client_name || ''}
            recipientEmail={invoice?.client_email || ''}
            invoiceNumber={invoice?.invoice_number || ''}
            isFullyPaid={job?.payment_status === 'PaidFully'}
            amountLabel={shareAmountLabel}
            jobId={job?.id ?? invoice?.job_id ?? jobId}
          />
        )}
      </>
    );
  }

  // When the job details modal is open, hide the invoice modal's backdrop so the
  // side menu is not blurred / blocked. Only the JobDetailsModal renders.
  if (showJobDetailsModal) {
    return createPortal(<LayerDepthContext.Provider value={layer.depth}>{renderModals()}</LayerDepthContext.Provider>, document.body);
  }

  return createPortal(
    <LayerDepthContext.Provider value={layer.depth}>
    <div className="fixed inset-0 overflow-y-auto" style={layer.style}>
      <div className="flex items-center justify-center min-h-screen p-4">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-secondary-900/50 backdrop-blur-sm" onClick={layer.onClose} />

        {/* Modal panel - Enlarged and Centered */}
        <div className="relative bg-white rounded-xl text-left overflow-hidden shadow-xl w-full max-w-7xl max-h-[92vh] flex flex-col">
          <div className="bg-white px-6 pt-5 pb-6 overflow-y-auto flex-1">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100">
                  <Receipt className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-secondary-900">
                    Invoice #{invoice.invoice_number}
                  </h3>
                  <p className="text-sm text-secondary-500">
                    {formatDateOnlyInCompanyTimeZone(invoice.date, companyTz)}
                    {invoice.due_date && ` • Due ${formatDateOnlyInCompanyTimeZone(invoice.due_date, companyTz)}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {job && (
                  <TakePaymentButton
                    job={job}
                    disabled={job.payment_status === 'PaidFully' || !job.total}
                    onPaymentComplete={() => {
                      queryClient.invalidateQueries({ queryKey: ['job', invoice?.job_id] });
                      queryClient.invalidateQueries({ queryKey: ['jobs'] });
                    }}
                  />
                )}
                <button
                  onClick={handleViewPdf}
                  disabled={isViewingPdf}
                  className="btn-secondary flex items-center gap-2 px-3 py-2"
                >
                  {isViewingPdf ? (
                    <LoadingSpinner />
                  ) : (
                    <FileText className="h-4 w-4" />
                  )}
                  {isViewingPdf ? 'Generating PDF...' : 'View PDF'}
                </button>
                {invoice.job_id && (
                  <button
                    onClick={() => setShowPdfAttachments(true)}
                    className="btn-secondary flex items-center gap-2 px-3 py-2"
                    title="Choose job attachments to add to the invoice PDF"
                  >
                    <Paperclip className="h-4 w-4" />
                    Invoice Attachments{pdfAttachmentCount > 0 ? ` (${pdfAttachmentCount})` : ''}
                  </button>
                )}
                <button
                  onClick={handleShareInvoice}
                  disabled={isSharing}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 rounded-xl shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="h-4 w-4" />
                  {isSharing ? 'Creating...' : 'Send'}
                </button>
                {job && (
                  <button
                    onClick={handleViewJob}
                    className="btn-primary flex items-center gap-2 px-3 py-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    View Job
                  </button>
                )}
                <button
                  onClick={layer.onClose}
                  className="p-2 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>


            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Left Column - Dates, Client Info, Status */}
              <div className="space-y-4">
                {/* Company name printed on this invoice */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <label className="text-xs font-semibold text-indigo-600 uppercase tracking-wide block mb-1">Company Name</label>
                  <EditableField
                    value={invoice.custom_company_name || ''}
                    type="text"
                    placeholder={defaultCompanyName || 'Add company name...'}
                    disabled={!canEditInvoice}
                    onSave={(val) => handleFieldSave('custom_company_name', val || null)}
                    inline={true}
                    renderValue={renderCompanyName}
                  />
                </div>

                {/* Dates - Editable */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-semibold text-blue-600 uppercase tracking-wide block mb-1">Invoice Date</label>
                      <EditableField
                        value={invoice.date ? invoice.date.split('T')[0] : ''}
                        type="date"
                        disabled={!canEditInvoice}
                        onSave={(val) => handleFieldSave('date', val)}
                        inline={true}
                        renderValue={(val) => (
                          <span className="text-gray-900 font-medium text-sm">
                            {val ? formatDateOnlyInCompanyTimeZone(val, companyTz) : 'Not set'}
                          </span>
                        )}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-blue-600 uppercase tracking-wide block mb-1">Due Date</label>
                      <EditableField
                        value={invoice.due_date ? invoice.due_date.split('T')[0] : ''}
                        type="date"
                        disabled={!canEditInvoice}
                        onSave={(val) => handleFieldSave('due_date', val || null)}
                        inline={true}
                        renderValue={(val) => (
                          <span className="text-gray-900 font-medium text-sm">
                            {val ? formatDateOnlyInCompanyTimeZone(val, companyTz) : <span className="text-gray-400">Not set</span>}
                          </span>
                        )}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-blue-600 uppercase tracking-wide block mb-1">PO #</label>
                      <EditableField
                        value={invoice.po || ''}
                        type="text"
                        placeholder="Add PO number..."
                        disabled={!canEditInvoice}
                        onSave={(val) => handleFieldSave('po', val || null)}
                        inline={true}
                        renderValue={(val) => (
                          <span className="text-gray-900 font-medium text-sm">
                            {val || <span className="text-gray-400">Not set</span>}
                          </span>
                        )}
                      />
                    </div>
                  </div>
                </div>

                {/* Client Information - Editable */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-semibold text-emerald-600 uppercase tracking-wide block mb-1">Client Name</label>
                      <EditableField
                        value={invoice.client_name || ''}
                        type="text"
                        placeholder="Add client name..."
                        disabled={!canEditInvoice}
                        onSave={(val) => handleFieldSave('client_name', val)}
                        inline={true}
                        renderValue={(val) => (
                          <span className="text-gray-900 text-sm">{val || <span className="text-gray-400 italic">No name</span>}</span>
                        )}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-emerald-600 uppercase tracking-wide flex items-center gap-1 mb-1">
                        <Phone className="h-3 w-3" /> Phone
                      </label>
                      <EditableField
                        value={invoice.client_phone || ''}
                        type="phone"
                        placeholder="Add phone number..."
                        disabled={!canEditInvoice}
                        onSave={(val) => handleFieldSave('client_phone', val)}
                        inline={true}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-emerald-600 uppercase tracking-wide flex items-center gap-1 mb-1">
                        <Mail className="h-3 w-3" /> Email
                      </label>
                      <EditableField
                        value={invoice.client_email || ''}
                        type="text"
                        placeholder="Add email..."
                        disabled={!canEditInvoice}
                        onSave={(val) => handleFieldSave('client_email', val)}
                        inline={true}
                        renderValue={(val) => (
                          <span className="text-gray-900 text-sm">{val || <span className="text-gray-400 italic">No email</span>}</span>
                        )}
                      />
                    </div>
                    {renderAddressField()}
                  </div>
                </div>

                {/* Invoice Status */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-3 text-sm">
                    {job && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Payment Status</span>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getPaymentStatusDisplay(job.payment_status).color}`}>
                          {getPaymentStatusDisplay(job.payment_status).label}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Viewed</span>
                      <span className={`font-medium ${invoice.viewed_at ? 'text-green-600' : 'text-gray-400'}`}>
                        {invoice.viewed_at
                          ? formatInstantInCompanyTimeZone(invoice.viewed_at, companyTz)
                          : 'Not viewed'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Sent</span>
                      <span className={`font-medium ${invoice.sent_at ? 'text-purple-600' : 'text-gray-400'}`}>
                        {invoice.sent_at
                          ? formatInstantInCompanyTimeZone(invoice.sent_at, companyTz)
                          : 'Not sent'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Signed</span>
                      <span className={`font-medium ${(invoice.signature && !invoice.is_signature_voided) ? 'text-green-600' : 'text-gray-400'}`}>
                        {(invoice.signature && !invoice.is_signature_voided) ? 'Yes' : 'Not signed'}
                      </span>
                    </div>
                    {invoice.signed_at && !invoice.is_signature_voided && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Signed at</span>
                        <span className="font-medium text-gray-900">
                          {formatInstantInCompanyTimeZone(invoice.signed_at, companyTz)}
                        </span>
                      </div>
                    )}
                    {invoice.signature && !invoice.is_signature_voided && (
                      <div className="border border-gray-200 rounded-md p-2 bg-gray-50 mt-2">
                        {invoice.signature.startsWith('data:') ||
                          invoice.signature.startsWith('http') ||
                          invoice.signature.startsWith('blob:') ? (
                          <img
                            src={invoice.signature}
                            alt="Client signature"
                            className="max-w-full h-20 object-contain mx-auto"
                          />
                        ) : (
                          <svg
                            viewBox="0 0 400 200"
                            className="w-full h-20"
                            role="img"
                            aria-label="Client signature"
                          >
                            <path
                              d={invoice.signature}
                              fill="none"
                              stroke="black"
                              strokeWidth="2"
                            />
                          </svg>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column - Invoice Items (extended) - Editable */}
              <div className="lg:col-span-3">
                {job ? (
                  <JobItemsSection
                    items={job.items || []}
                    companyId={job.company?.id}
                    taxRate={taxRate}
                    defaultItemTaxable={companyInvoiceSettings?.default_item_taxable ?? true}
                    onItemsChange={handleJobItemsChange}
                    isEditable={canEditItems}
                  />
                ) : (
                  <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-500 text-center h-full flex items-center justify-center">
                    No job linked — items cannot be edited.
                  </div>
                )}

                {/* Payment Summary below items */}
                {job && (() => {
                  const items = job.items || invoice.items || [];
                  return (
                    <div className="border-t border-gray-200 p-4 bg-gray-50 rounded-b-lg shrink-0 mt-0">
                      <div className="flex justify-end">
                        <div className="w-64 space-y-2">
                          {(job.payments?.length ?? 0) > 0 && (() => {
                            const totalPaid = (job.payments ?? []).reduce((s: number, p: PaymentDTO) => s + p.amount, 0);
                            const balance = calculateTotal(items, taxRate) - totalPaid;
                            return (
                              <>
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">Paid</span>
                                  <span className="text-green-600 font-medium">-${totalPaid.toFixed(2)}</span>
                                </div>
                                <div className="border-t border-gray-300 pt-2 mt-2">
                                  <div className="flex justify-between text-base font-bold">
                                    <span className="text-gray-900">Balance Due</span>
                                    <span className={balance <= 0 ? 'text-green-600' : 'text-red-600'}>
                                      ${Math.max(0, balance).toFixed(2)}
                                    </span>
                                  </div>
                                </div>
                              </>
                            );
                          })()}
                          <div className="flex justify-between items-center text-sm pt-2">
                            <span className="text-gray-600">Payment Status</span>
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getPaymentStatusDisplay(job.payment_status).color}`}>
                              {getPaymentStatusDisplay(job.payment_status).label}
                            </span>
                          </div>
                          <div className="pt-2">
                            <JobDownPaymentRow job={job} disabled={!canEditInvoice} />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Notes */}
                <div className="bg-gray-50 rounded-lg p-4 mt-4">
                  <div>
                    <label className="text-xs font-semibold text-amber-600 uppercase tracking-wide block mb-1">Notes</label>
                    <EditableField
                      value={invoice.notes || ''}
                      type="textarea"
                      placeholder="Add notes..."
                      disabled={!canEditInvoice}
                      onSave={(val) => handleFieldSave('notes', val || null)}
                      inline={true}
                      renderValue={(val) => (
                        <span className="text-gray-900 text-sm whitespace-pre-wrap">{val || <span className="text-gray-400 italic">Notes will appear at bottom of your invoice</span>}</span>
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {renderModals()}
    </div>
    </LayerDepthContext.Provider>,
    document.body
  );
}
